import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../static/styles/WeeklyTournaments.module.css';
import calendarIcon from '../static/img/Home/calendarIcon.svg';
import { fetchWeeklyTournaments, fetchEslTournaments, eslTournamentCheckin, eslTournamentMatchCheckin } from '../api/tournaments';

import pubgIcon from '../static/img/DailyGamingTasks/iconPubgDailyTasks.png';
import apexIcon from '../static/img/DailyGamingTasks/iconApexDailyTasks.avif';
import codIcon from '../static/img/DailyGamingTasks/iconCODDailyTasks.avif';
import freeFireIcon from '../static/img/DailyGamingTasks/iconFreeFireDailyTasks.avif';
import MessagePopup from '../components/MessagePopup';
import CannonClashInstructions from '../components/CannonClashInstructions';
import AimTrainInstructions from '../components/AimTrainInstructions';
import popupStyles from '../static/styles/MessagePopup.module.css';
import cannonClashCover from '../static/img/MadeByESL/coverCannonClash.jpeg';
import aimTrainCover from '../static/img/MadeByESL/aimTrainBattle.avif';

// Import static background images
import pubgBg from '../static/img/WeeklyTournaments/imagePubgWeeklyTournament.avif';
import freeFireBg from '../static/img/WeeklyTournaments/imageFreeFireWeeklyTournament.avif';
import { jwtDecode } from 'jwt-decode';

const getGameIcon = (gameName) => {
    switch (gameName.toLowerCase()) {
        case 'pubg mobile':
            return pubgIcon;
        case 'freefire':
            return freeFireIcon;
        // Add cases for other games like COD, APEX LEGENDS
        default:
            return null; // Or a default icon
    }
};

// Map game names to static background images
const getGameBackground = (gameName) => {
    switch (gameName.toLowerCase()) {
        case 'pubg mobile':
            return pubgBg;
        case 'freefire':
            return freeFireBg;
        // Add cases for other games
        default:
            return null; // Fallback or default background
    }
};

// Helper function to format date/time
const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return { date: 'N/A', time: 'N/A' };
    const [datePart, timePart, ampm] = dateTimeString.split(' ');
    return {
        date: datePart,
        time: `${timePart} ${ampm}`
    };
};

const WeeklyTournaments = () => {
    const [tournaments, setTournaments] = useState([]);
    const [eslTournaments, setEslTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRoomPopup, setShowRoomPopup] = useState(false);
    const [roomId, setRoomId] = useState('');
    const [selectedTournament, setSelectedTournament] = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [showInstructions, setShowInstructions] = useState(false);
    const [showAimTrainInstructions, setShowAimTrainInstructions] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Tournaments | Jazz Esl"
        
        // Check if user is logged in
        const accessToken = sessionStorage.getItem('access_token');
        const msisdn = sessionStorage.getItem('msisdn');
        setIsLoggedIn(!!accessToken && !!msisdn);
        
        const loadTournaments = async () => {
            try {
                setLoading(true);
                setError(null);
                const [weeklyData, eslData] = await Promise.all([
                    fetchWeeklyTournaments(),
                    fetchEslTournaments()
                ]);
                setTournaments(weeklyData);
                setEslTournaments(eslData);
            } catch (err) {
                setError(err.message || 'Failed to load tournaments');
            } finally {
                setLoading(false);
            }
        };

        loadTournaments();
    }, []);

    if (loading) {
        return <div className={styles.loading}>Loading tournaments...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error: {error}</div>;
    }

    const handleJoinTournament = (tournamentId) => {
        navigate(`/join-tournament?tournamentId=${tournamentId}`);
    };

    const handleEslTournamentJoin = async (tournament) => {
        if (!isLoggedIn) {
            setPopupMessage('Please log in to participate in tournaments');
            setPopupOpen(true);
            return;
        }

        try {
            if (tournament.is_running === 1) {
                //console.log('Tournament is running, showing room popup');
                setSelectedTournament(tournament);
                setShowRoomPopup(true);
                return;
            }

            const response = await eslTournamentCheckin(tournament.tourn_id);
            //console.log('ESL Tournament Checkin Response:', response);
            
            if (response.data.already_submited === true) {
                if (tournament.is_running === 1) {
                    //console.log('Already submitted and tournament is running, showing room popup');
                    setSelectedTournament(tournament);
                    setShowRoomPopup(true);
                } else {
                    setPopupMessage('You are already registered for this tournament. Wait for it to start.');
                    setPopupOpen(true);
                }
            } else {
                const isCannonClash = tournament.tournament_game === "cannon_clash_001";
                if (isCannonClash) {
                    setPopupMessage('Successfully joined the tournament! Check the instructions below for how to play.');
                    setPopupOpen(true);
                    setTimeout(() => {
                        setShowInstructions(true);
                    }, 1500);
                } else {
                    setPopupMessage('Successfully joined the tournament!');
                    setPopupOpen(true);
                }
            }
        } catch (error) {
            //console.error('ESL Tournament Join Error:', error);
            setPopupMessage(error.message || 'Failed to join tournament');
            setPopupOpen(true);
        }
    };

    const handleRoomIdSubmit = async () => {
        if (!roomId.trim() || !selectedTournament) return;

        try {
            const response = await eslTournamentMatchCheckin(selectedTournament.tourn_id, roomId.trim());
            //console.log('Match Checkin Response:', response);
            
            // Check for both 'success' and 'successss' (typo in API)
            const isSuccess = response.success === true || response.successss === true;
            
            if (!isSuccess) {
                // Case 1: Error (too early, invalid room ID, etc.)
                setPopupMessage(response.message || 'Failed to check in to match');
                setPopupOpen(true);
            } else {
                // Case 2 & 3: Navigate to game with roomId
                const gameRoute = selectedTournament.tournament_game === "aim_train_001" ? '/aim-train' : '/cannon-clash';
                navigate(gameRoute, {
                    state: {
                        roomId: roomId.trim(),
                        tournamentId: selectedTournament.tourn_id,
                        startGame: response.start_game,
                        tournamentMode: true
                    }
                });
            }
        } catch (error) {
            //console.error('Match Checkin Error:', error);
            setPopupMessage(error.message || 'Failed to check in to match');
            setPopupOpen(true);
        } finally {
            setShowRoomPopup(false);
            setRoomId('');
            setSelectedTournament(null);
        }
    };

    const handleRoomPopupClose = () => {
        setShowRoomPopup(false);
        setRoomId('');
        setSelectedTournament(null);
    };

    const handlePopupClose = () => {
        setPopupOpen(false);
    };

    const handleInstructionsClose = () => {
        setShowInstructions(false);
    };

    const handleAimTrainInstructionsClose = () => {
        setShowAimTrainInstructions(false);
    };

    const handleShowInstructions = (tournament) => {
        if (tournament.tournament_game === "cannon_clash_001") {
            setShowInstructions(true);
        } else if (tournament.tournament_game === "aim_train_001") {
            setShowAimTrainInstructions(true);
        }
    };

    return (
        <div className={styles.container}>
            <h2 className={styles.heading}>Weekly Tournaments</h2>
            <p className={styles.subheading}>Compete in tournaments to win credits</p>
            
            {eslTournaments.length > 0 && (
                <div className={styles.eslSection}>
                    <div className={styles.sectionSeparator}>
                        <span className={styles.sectionSeparatorText}>ESL Tournaments</span>
                    </div>
                    <div className={styles.cardGrid}>
                        {eslTournaments.map((tournament) => (
                            <div key={tournament.Id} className={styles.cardContainer}>
                                <div className={styles.card}>
                                    <div 
                                        className={styles.cardContent}
                                        style={{
                                            backgroundImage: tournament.tournament_game === "cannon_clash_001"
                                                ? `url(${cannonClashCover})`
                                                : tournament.tournament_game === "aim_train_001"
                                                    ? `url(${aimTrainCover})`
                                                    : tournament.thumbnail
                                                        ? `url(${tournament.thumbnail})`
                                                        : 'none'
                                        }}
                                    >
                                        {tournament.status === 4 ? (
                                            <div className={styles.completedOverlay}>
                                                <h3>Tournament has ended</h3>
                                            </div>
                                        ) : (
                                            <div className={styles.bottomContent}>
                                                <div className={styles.gameIcon}>
                                                    <img 
                                                        src={tournament.thumbnail} 
                                                        alt={tournament.tourn_name}
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                </div>
                                                <div className={styles.timeInfo}>
                                                    <img src={calendarIcon} alt="Calendar" />
                                                    <div>
                                                        <p>{formatDateTime(tournament.time).date}</p>
                                                        <p>{formatDateTime(tournament.time).time}</p>
                                                    </div>
                                                </div>
                                                <div className={styles.rightInfo}>
                                                    {tournament.is_running === 1 && (
                                                        <div className={styles.runningIndicator}>
                                                            <p>🔴 LIVE</p>
                                                        </div>
                                                    )}
                                                    <div className={styles.prizes}>
                                                        {tournament.prize && <p>{tournament.prize} Credits</p>}
                                                        {tournament.total_checkin && <p>Participants: {tournament.total_checkin}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {tournament.is_running === 1 && (
                                            <button 
                                                className={styles.detailsButton}
                                                onClick={() => navigate(`/tournament/${tournament.tourn_id}`)}
                                                title="View Tournament Details"
                                            >
                                                <span className={styles.detailsIcon}>📊</span>
                                                <span className={styles.detailsText}>Details</span>
                                            </button>
                                        )}
                                        {(tournament.tournament_game === "cannon_clash_001" || tournament.tournament_game === "aim_train_001") && tournament.status !== 4 && (
                                            <button
                                                className={styles.infoButton}
                                                onClick={() => handleShowInstructions(tournament)}
                                                title="View Tournament Instructions"
                                            >
                                                <span className={styles.infoIcon}>?</span>
                                                <span className={styles.infoText}>How to Play</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <button 
                                    className={styles.joinButton}
                                    onClick={() => handleEslTournamentJoin(tournament)}
                                    style={{ display: tournament.status === 4 ? 'none' : 'block' }}
                                >
                                    {!isLoggedIn ? 'Login Required' : (tournament.is_running === 1 ? 'Play Now' : 'Join now')}
                                </button> 
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {eslTournaments.length > 0 && tournaments.length > 0 && (
                <div className={styles.sectionSeparator}>
                    <span className={styles.sectionSeparatorText}>Weekly Tournaments</span>
                </div>
            )}

            <div className={styles.cardGrid}>
                {tournaments.map((tournament) => {
                    const { date, time } = formatDateTime(tournament.time);
                    const isCompleted = tournament.status !== 1 || tournament.is_running === 1;
                    const gameIconSrc = getGameIcon(tournament.tourn_name);
                    const gameBgSrc = getGameBackground(tournament.tourn_name);

                    return (
                        <div key={tournament.id} className={styles.cardContainer}>
                            <div className={styles.card}>
                                <div 
                                    className={styles.cardContent}
                                    style={{ backgroundImage: gameBgSrc ? `url(${gameBgSrc})` : 'none' }}
                                >
                                    {isCompleted ? (
                                        <div className={styles.completedOverlay}>
                                            <h3>Tournament has ended</h3>
                                            <p>View Details</p> 
                                        </div>
                                    ) : (
                                        <div className={styles.bottomContent}>
                                            <div className={styles.gameIcon}>
                                                {gameIconSrc && <img src={gameIconSrc} alt={tournament.tourn_name} />}
                                            </div>
                                            <div className={styles.timeInfo}>
                                                <img src={calendarIcon} alt="Calendar" />
                                                <div>
                                                    <p>Starting At</p>
                                                    <p>{date}</p>
                                                    <p>{time}</p>
                                                </div>
                                            </div>
                                            <div className={styles.rightInfo}>
                                                <h3 className={styles.entryFee}>
                                                    {
                                                        tournament.entryFee != null ? 
                                                            `${tournament.entryFee} Credits` : // If entryFee has a value
                                                            (tournament.isEntryFree === false ? 'N/A' : 'Free Entry') // If entryFee is null, check isEntryFree. False means N/A, otherwise (true or null) it's Free.
                                                    }
                                                </h3>
                                                <div className={styles.prizes}>
                                                    {tournament.prize && <p>1st: {tournament.prize} Credits</p>}
                                                    {tournament.second_prize && <p>2nd: {tournament.second_prize} Credits</p>}
                                                    {tournament.third_prize && <p>3rd: {tournament.third_prize} Credits</p>}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button 
                                className={styles.joinButton}
                                onClick={() => handleJoinTournament(tournament.tourn_id)}
                            >
                                Join now
                            </button> 
                        </div>
                    );
                })}
            </div>

            {/* Room ID Popup */}
            {showRoomPopup && (
                <div className={popupStyles.popupOverlay}>
                    <div className={popupStyles.popupContent}>
                        <h3>Enter Room ID</h3>
                        <p>You are already checked in to this tournament. Please enter your room ID to continue:</p>
                        <input
                            type="text"
                            value={roomId}
                            onChange={(e) => setRoomId(e.target.value)}
                            placeholder="Enter room ID"
                            className={popupStyles.roomInput}
                        />
                        <div className={popupStyles.popupButtons}>
                            <button onClick={handleRoomIdSubmit} className={popupStyles.submitButton}>
                                Submit
                            </button>
                            <button onClick={handleRoomPopupClose} className={popupStyles.cancelButton}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <MessagePopup
                isOpen={popupOpen}
                message={popupMessage}
                onClose={handlePopupClose}
            />

            <CannonClashInstructions
                isOpen={showInstructions}
                onClose={handleInstructionsClose}
            />

            <AimTrainInstructions
                isOpen={showAimTrainInstructions}
                onClose={handleAimTrainInstructionsClose}
            />
        </div>
    );
};

export default WeeklyTournaments; 